// import React, { useState, useEffect } from 'react';
// import { Trash2, Crown } from 'lucide-react';
// import api from '../services/api';
// import toast from 'react-hot-toast';

// const Users = () => {
//   const [users, setUsers] = useState([]);

//   useEffect(() => {
//     fetchUsers();
//   }, []);

//   const fetchUsers = async () => {
//     const { data } = await api.get('/users');
//     setUsers(data.data);
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm('Delete user?')) return;
//     try {
//       await api.delete(`/users/${id}`);
//       toast.success('User deleted');
//       fetchUsers();
//     } catch (error) {
//       toast.error('Error deleting user');
//     }
//   };

//   return (
//     <div className="space-y-6">
//       <div>
//         <h1 className="text-3xl font-bold mb-2">Users</h1>
//         <p className="text-gray-400">Manage platform users</p>
//       </div>

//       <div className="gradient-card border border-gray-800 rounded-xl overflow-hidden">
//         <table className="w-full">
//           <thead className="bg-dark">
//             <tr className="text-left border-b border-gray-800">
//               <th className="px-6 py-4">User</th>
//               <th className="px-6 py-4">Email</th>
//               <th className="px-6 py-4">Role</th>
//               <th className="px-6 py-4">Level</th>
//               <th className="px-6 py-4">Points</th>
//               <th className="px-6 py-4">Joined</th>
//               <th className="px-6 py-4">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {users.map(user => (
//               <tr key={user._id} className="border-b border-gray-800 hover:bg-dark/50">
//                 <td className="px-6 py-4">
//                   <div className="flex items-center gap-3">
//                     <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center font-bold">
//                       {user.username?.charAt(0).toUpperCase()}
//                     </div>
//                     <span>{user.username}</span>
//                   </div>
//                 </td>
//                 <td className="px-6 py-4 text-gray-400">{user.email}</td>
//                 <td className="px-6 py-4">
//                   {user.role === 'admin' ? (
//                     <span className="flex items-center gap-1 text-yellow-500"><Crown size={16} /> Admin</span>
//                   ) : (
//                     <span className="text-gray-400">User</span>
//                   )}
//                 </td>
//                 <td className="px-6 py-4">{user.level || 1}</td>
//                 <td className="px-6 py-4">{user.points || 0}</td>
//                 <td className="px-6 py-4 text-gray-400">{new Date(user.createdAt).toLocaleDateString()}</td>
//                 <td className="px-6 py-4">
//                   {user.role !== 'admin' && (
//                     <button onClick={() => handleDelete(user._id)} className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30">
//                       <Trash2 size={16} />
//                     </button>
//                   )}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default Users;



import React, { useState, useEffect } from 'react';
import { Trash2, Crown, Ban, CheckCircle, Search, Filter, Shield, MessageSquare } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, blocked, verified
  const [filterRole, setFilterRole] = useState('all'); // all, admin, user
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, filterStatus, filterRole]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data.data);
    } catch (error) {
      toast.error('Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(user => user.isActive && !user.isChatBlocked);
    } else if (filterStatus === 'blocked') {
      filtered = filtered.filter(user => user.isChatBlocked);
    } else if (filterStatus === 'verified') {
      filtered = filtered.filter(user => user.isVerified);
    }

    // Role filter
    if (filterRole !== 'all') {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    setFilteredUsers(filtered);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete user?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  const handleBlockUser = async (userId, username, isBlocked) => {
    if (isBlocked) {
      // Unblock user
      try {
        await api.post(`/chat/admin/unblock-user/${userId}`);
        toast.success('User unblocked from chat');
        fetchUsers();
      } catch (error) {
        toast.error('Failed to unblock user');
      }
    } else {
      // Block user
      const duration = prompt(`Block ${username} from chat? Enter days (0 for permanent):`);
      if (duration === null) return;
      
      const reason = prompt('Enter reason:');
      if (!reason) return;

      try {
        await api.post('/chat/admin/block-user', { 
          userId, 
          duration: parseInt(duration), 
          reason 
        });
        toast.success('User blocked from chat');
        fetchUsers();
      } catch (error) {
        toast.error('Failed to block user');
      }
    }
  };

  const handleVerifyUser = async (userId, isVerified) => {
    try {
      if (isVerified) {
        await api.post(`/chat/admin/unverify-user/${userId}`);
        toast.success('User unverified');
      } else {
        await api.post(`/chat/admin/verify-user/${userId}`);
        toast.success('User verified');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleSetBadge = async (userId, username) => {
    const badge = prompt(
      `Set badge for ${username}:\n\n` +
      'Options:\n' +
      '• none - Remove badge\n' +
      '• verified - Verified user\n' +
      '• master - Master player\n' +
      '• top_rated - Top rated\n' +
      '• vip - VIP member\n' +
      '• moderator - Moderator'
    );
    
    if (!badge) return;
    
    const validBadges = ['none', 'verified', 'master', 'top_rated', 'vip', 'moderator'];
    if (!validBadges.includes(badge)) {
      toast.error('Invalid badge');
      return;
    }

    try {
      await api.post(`/chat/admin/set-badge/${userId}`, { badge });
      toast.success('Badge updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update badge');
    }
  };

  const getBadgeDisplay = (badge) => {
    const badges = {
      verified: { icon: '✓', color: '#00D9FF', label: 'Verified' },
      master: { icon: '👑', color: '#FFD700', label: 'Master' },
      top_rated: { icon: '⭐', color: '#FFA502', label: 'Top Rated' },
      vip: { icon: '💎', color: '#9B59B6', label: 'VIP' },
      moderator: { icon: '🛡️', color: '#E74C3C', label: 'Moderator' }
    };
    
    return badges[badge] || null;
  };

  const getStats = () => {
    return {
      total: users.length,
      active: users.filter(u => u.isActive && !u.isChatBlocked).length,
      blocked: users.filter(u => u.isChatBlocked).length,
      verified: users.filter(u => u.isVerified).length
    };
  };

  const stats = getStats();

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">Users Management</h1>
          <p className="text-gray-400">Manage platform users</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Users</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <Crown className="text-primary" size={32} />
          </div>
        </div>
        
        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Active Users</p>
              <p className="text-2xl font-bold text-green-500">{stats.active}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Blocked Users</p>
              <p className="text-2xl font-bold text-red-500">{stats.blocked}</p>
            </div>
            <Ban className="text-red-500" size={32} />
          </div>
        </div>

        <div className="gradient-card border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Verified Users</p>
              <p className="text-2xl font-bold text-blue-500">{stats.verified}</p>
            </div>
            <Shield className="text-blue-500" size={32} />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="gradient-card border border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by username or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-dark border border-gray-700 rounded-lg focus:outline-none focus:border-primary text-white"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 bg-dark border border-gray-700 rounded-lg focus:outline-none focus:border-primary text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="blocked">Blocked Only</option>
            <option value="verified">Verified Only</option>
          </select>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-4 py-3 bg-dark border border-gray-700 rounded-lg focus:outline-none focus:border-primary text-white"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
        </div>

        {/* Filter Summary */}
        <div className="mt-4 text-sm text-gray-400">
          Showing {filteredUsers.length} of {users.length} users
          {searchTerm && ` • Search: "${searchTerm}"`}
          {filterStatus !== 'all' && ` • Status: ${filterStatus}`}
          {filterRole !== 'all' && ` • Role: ${filterRole}`}
        </div>
      </div>

      {/* Users Table */}
      <div className="gradient-card border border-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-dark">
            <tr className="text-left border-b border-gray-800">
              <th className="px-6 py-4">User</th>
              <th className="px-6 py-4">Email</th>
              <th className="px-6 py-4">Role</th>
              <th className="px-6 py-4">Level</th>
              <th className="px-6 py-4">Points</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Joined</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className="border-b border-gray-800 hover:bg-dark/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center font-bold">
                      {user.username?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{user.username}</span>
                        {user.isVerified && <span className="text-blue-400">✓</span>}
                        {getBadgeDisplay(user.badge) && (
                          <span 
                            className="text-xs px-2 py-1 rounded-full"
                            style={{ 
                              backgroundColor: getBadgeDisplay(user.badge).color + '20', 
                              color: getBadgeDisplay(user.badge).color 
                            }}
                          >
                            {getBadgeDisplay(user.badge).icon} {getBadgeDisplay(user.badge).label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-400">{user.email}</td>
                <td className="px-6 py-4">
                  {user.role === 'admin' ? (
                    <span className="flex items-center gap-1 text-yellow-500">
                      <Crown size={16} /> Admin
                    </span>
                  ) : (
                    <span className="text-gray-400">User</span>
                  )}
                </td>
                <td className="px-6 py-4">{user.level || 1}</td>
                <td className="px-6 py-4">{user.points || 0}</td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {user.isChatBlocked ? (
                      <div>
                        <span className="px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-xs block w-fit">
                          Chat Blocked
                        </span>
                        {user.chatBlockedUntil && (
                          <span className="text-xs text-gray-500">
                            Until: {new Date(user.chatBlockedUntil).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ) : user.isActive ? (
                      <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-xs">Active</span>
                    ) : (
                      <span className="px-3 py-1 bg-gray-500/20 text-gray-500 rounded-full text-xs">Inactive</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-400">{new Date(user.createdAt).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  {user.role !== 'admin' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleBlockUser(user.id, user.username, user.isChatBlocked)}
                        className={`p-2 rounded-lg ${
                          user.isChatBlocked
                            ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                            : 'bg-orange-500/20 text-orange-500 hover:bg-orange-500/30'
                        }`}
                        title={user.isChatBlocked ? 'Unblock from Chat' : 'Block from Chat'}
                      >
                        <Ban size={16} />
                      </button>

                      <button
                        onClick={() => handleVerifyUser(user.id, user.isVerified)}
                        className={`p-2 rounded-lg ${
                          user.isVerified 
                            ? 'bg-gray-500/20 text-gray-500 hover:bg-gray-500/30' 
                            : 'bg-blue-500/20 text-blue-500 hover:bg-blue-500/30'
                        }`}
                        title={user.isVerified ? 'Unverify' : 'Verify'}
                      >
                        <CheckCircle size={16} />
                      </button>

                      <button
                        onClick={() => handleSetBadge(user.id, user.username)}
                        className="p-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30"
                        title="Set Badge"
                      >
                        <Crown size={16} />
                      </button>

                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;





